// Per-conversation W3C trace context for CMSP chat calls.
//
// Every request of one conversation (init, stream turns, live polls, transcript, attachments) carries
// the same trace id so the whole conversation lands in one distributed trace, while each request gets a
// fresh parent span id. Starting a new chat mints a new conversation id and therefore a new trace id, so
// conversations never chain onto each other's trace (previously a page-level trace context made every
// chat started on the same page share one operation id).
//
// The trace id is random rather than derived from the conversation id: the conversation id acts as the
// credential for anonymous conversations, and traceparent is propagated to downstream services.

const MAX_TRACKED_CONVERSATIONS = 64;
const traceIds = new Map();

function randomHex(byteLength) {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  // All-zero ids are invalid per W3C Trace Context; astronomically unlikely, but never emit one.
  return /^0+$/.test(hex) ? randomHex(byteLength) : hex;
}

/**
 * Returns the trace id (32 lowercase hex chars) owned by `conversationId`, minting one on first use.
 * @param {string} conversationId
 * @returns {string}
 */
export function getConversationTraceId(conversationId) {
  let traceId = traceIds.get(conversationId);
  if (!traceId) {
    traceId = randomHex(16);
    traceIds.set(conversationId, traceId);
    if (traceIds.size > MAX_TRACKED_CONVERSATIONS) {
      traceIds.delete(traceIds.keys().next().value);
    }
  }
  return traceId;
}

/**
 * Builds a `traceparent` header value for one request of `conversationId`: the conversation's trace id
 * plus a new span id. Returns undefined when there is no conversation id, leaving the server to start
 * its own trace.
 * @param {string | null | undefined} conversationId
 * @returns {string | undefined}
 */
export function createConversationTraceparent(conversationId) {
  if (!conversationId) return undefined;
  return `00-${getConversationTraceId(conversationId)}-${randomHex(8)}-01`;
}

/**
 * Returns a copy of `headers` with the conversation's `traceparent` set (replacing any existing one).
 * @template {Record<string, string>} T
 * @param {T} headers
 * @param {string | null | undefined} conversationId
 * @returns {T}
 */
export function withConversationTrace(headers, conversationId) {
  const traceparent = createConversationTraceparent(conversationId);
  if (!traceparent) return headers;
  const next = { ...headers };
  for (const key of Object.keys(next)) {
    if (key.toLowerCase() === 'traceparent') delete next[key];
  }
  next.traceparent = traceparent;
  return next;
}
